import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Invite not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/invites/${token}/accept`, form);
      localStorage.setItem("lf_token", r.data.token);
      toast.success(`Welcome to ${preview.workspace_name}`);
      await refresh();
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invite");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]"><Loader2 className="h-5 w-5 animate-spin text-[#5C5C66]" /></div>;
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAFAF7]">
      <div className="hidden lg:flex flex-col justify-between p-12 dark-section relative overflow-hidden">
        <div className="absolute inset-0 dot-bg-dark opacity-50" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full bg-[#FF5722]/20 blur-3xl" />
        <div className="relative"><Brand light /></div>
        <div className="relative">
          <div className="label-mono !text-white/50 mb-4">— Workspace invite</div>
          <div className="font-display text-5xl font-black tracking-tighter leading-tight text-white">
            You've been<br />invited.
          </div>
          {preview && (
            <p className="mt-6 text-white/70 max-w-sm">
              {preview.inviter_name} added you to <b className="text-white">{preview.workspace_name}</b> as a <b className="text-white">{preview.role}</b>.
            </p>
          )}
        </div>
        <div className="relative text-xs font-mono uppercase tracking-widest text-white/40">© 2026 LeadsB2B</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Brand /></div>

          {error ? (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 text-sm">
                <AlertCircle className="h-4 w-4 text-red-700 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-red-900 mb-1">Can't open this invite</div>
                  <div className="text-red-800">{error}</div>
                </div>
              </div>
              <Link to="/login" className="block"><Button variant="outline" className="w-full rounded-lg">Go to sign in</Button></Link>
            </div>
          ) : preview && (
            <form onSubmit={accept}>
              <div className="label-mono mb-3">— Accept invite</div>
              <h1 className="font-display text-3xl font-black tracking-tighter mb-2">Join {preview.workspace_name}.</h1>
              <p className="text-sm text-[#5C5C66] mb-6">Set your password to create your account at <b>{preview.email}</b>.</p>
              <div className="space-y-5">
                <div>
                  <Label htmlFor="email" className="label-mono">Email</Label>
                  <Input id="email" value={preview.email} readOnly className="mt-2 h-11 rounded-lg bg-[#FAFAF7]" />
                </div>
                <div>
                  <Label htmlFor="name" className="label-mono">Your name</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 rounded-lg" data-testid="invite-accept-name" />
                </div>
                <div>
                  <Label htmlFor="password" className="label-mono">Create password</Label>
                  <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 h-11 rounded-lg" data-testid="invite-accept-password" />
                </div>
                <div className="flex items-center gap-2 text-xs text-[#5C5C66]">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" /> Joining as <b className="text-[#0A0A0F]">{preview.role}</b>
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-11 rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white font-medium" data-testid="invite-accept-submit">
                  {submitting ? "Joining…" : "Accept & join"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
